import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import CheckIcon from '@mui/icons-material/Check'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined'
import { useThemeMode } from '../theme/ThemeModeProvider'
import { ICON, TEXT } from '../theme/tokens'

export const THEME_OPTIONS = [
    { value: 'light', label: 'Light', Icon: LightModeOutlinedIcon },
    { value: 'dark', label: 'Dark', Icon: DarkModeOutlinedIcon },
    {
        value: 'system',
        label: 'Match device',
        Icon: SettingsBrightnessOutlinedIcon,
    },
]

/**
 * The header's theme control: an icon showing the current preference that
 * opens a menu of Light, Dark and Match device, the chosen one ticked.
 */
const ThemeSwitch = () => {
    const { preference, mode, setPreference } = useThemeMode()
    const [anchor, setAnchor] = useState(null)
    const current =
        THEME_OPTIONS.find((o) => o.value === preference) || THEME_OPTIONS[2]
    const CurrentIcon = current.Icon
    // "Theme: Match device (dark)" tells a keyboard or screen reader user
    // what they see as well as what they chose.
    const name =
        preference === 'system'
            ? `Theme: ${current.label} (${mode})`
            : `Theme: ${current.label}`

    return (
        <>
            <Tooltip title={name}>
                <IconButton
                    size="small"
                    aria-label={`${name}. Change theme`}
                    aria-haspopup="menu"
                    aria-expanded={anchor ? 'true' : undefined}
                    aria-controls={anchor ? 'theme-menu' : undefined}
                    onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{ color: TEXT.secondary }}
                >
                    <CurrentIcon sx={{ fontSize: ICON.lg }} />
                </IconButton>
            </Tooltip>
            <Menu
                id="theme-menu"
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {THEME_OPTIONS.map(({ value, label, Icon }) => (
                    <MenuItem
                        key={value}
                        selected={value === preference}
                        onClick={() => {
                            setPreference(value)
                            setAnchor(null)
                        }}
                    >
                        <ListItemIcon sx={{ color: TEXT.secondary }}>
                            <Icon sx={{ fontSize: ICON.md }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={label}
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                        {value === preference && (
                            <CheckIcon
                                aria-label="Selected"
                                sx={{
                                    fontSize: ICON.md,
                                    ml: 2,
                                    color: TEXT.primary,
                                }}
                            />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

export default ThemeSwitch
